import { useEffect, useRef } from "react";
import { Container, CssBaseline, Grid } from "@mui/material";
import { Navigate, Outlet, useMatch } from "react-router-dom";
import { appPrefix, appDefaultRoute } from "pages/app-taxi-ranking";
import { Applications } from "configs";
import { useAppDispatch } from "store";
import { setRootPath } from "reducers/auth.reducer";
import { setArchivedLeafLocations } from "reducers/newLocation.reducer";
import { newExecuteRetrieveLocationsRequest } from "api/common/side-panel.api";
import { useShouldRenderPageGuard } from "hooks/useShouldRenderPageGuard";
import LoadingLayout from "layouts/common/loader/LoadingLayout.tsx";
import SidebarTaxiRank from "./drawer/SidebarTaxiRank";
import NavbarTaxiRank from "./header/NavbarTaxiRank";

const { AppIdTaxiRanking } = Applications;

export default function PlateSearchReportLayout() {
    const match = useMatch(appPrefix);
    const { shouldRender } = useShouldRenderPageGuard(AppIdTaxiRanking);
    const dispatch = useAppDispatch();
    const archivedLocationsAbortRef = useRef<AbortController | null>(null);

    useEffect(() => {
        dispatch(setRootPath("/taxi-ranking/home"));
    }, []);

    // Old reports can reference locations that have since been archived. The sidebar only
    // loads active locations, so fetch the archived-inclusive tree once on entry and keep
    // only the archived leaves — the report tables use them to resolve those location names.
    useEffect(() => {
        (async () => {
            try {
                archivedLocationsAbortRef.current = new AbortController();
                const { signal } = archivedLocationsAbortRef.current;
                const locationTree = (await newExecuteRetrieveLocationsRequest(
                    { signal },
                    { showArchived: true },
                ));
                if (Array.isArray(locationTree)) {
                    dispatch(setArchivedLeafLocations(locationTree));
                }
            } catch (error) {
                console.error(error);
            }
        })();
        return () => {
            archivedLocationsAbortRef.current?.abort();
        };
    }, []);

    if (match) {
        return <Navigate to={appDefaultRoute} replace />;
    }

    return shouldRender ? (
        <Container maxWidth="xl" disableGutters>
            <CssBaseline />
            <NavbarTaxiRank />
            <Grid className="custom-container">
                <SidebarTaxiRank rootPath="/taxi-ranking/home" />

                <div className="content">
                    <Outlet />
                </div>
            </Grid>
        </Container>
    ) : (
        <LoadingLayout />
    );
}
